import { useCallback, useRef, useState } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

export const SWATCH_SIZE = 24
const SWATCH_GAP = 8
const SWATCH_SLOT = SWATCH_SIZE + SWATCH_GAP
// Horizontal space (px) held back for the trailing "+X" overflow link plus its
// preceding gap, so the last visible swatch isn't clipped behind it under
// `overflow: hidden`. Sized for the 1-2 digit counts we render (the link is a
// body-14 "+N", digits only); much larger counts could still overrun it.
const OVERFLOW_RESERVE = 32
const HARD_CAP = 5

export function computeVisibleSwatchCount(availableWidth: number, total: number): number {
  if (total <= 0) return 0
  if (availableWidth <= 0) return Math.min(HARD_CAP, total)

  const fitsWithoutOverflow = Math.floor((availableWidth + SWATCH_GAP) / SWATCH_SLOT)
  if (total <= HARD_CAP && fitsWithoutOverflow >= total) return total

  const fitsWithOverflow = Math.floor((availableWidth + SWATCH_GAP - OVERFLOW_RESERVE) / SWATCH_SLOT)
  return Math.max(1, Math.min(HARD_CAP, fitsWithOverflow))
}

export function useVisibleSwatchCount(total: number): [(node: HTMLElement | null) => void, number] {
  const [width, setWidth] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!isBrowser() || !node || typeof ResizeObserver === 'undefined') return

    const update = () => {
      setWidth(node.getBoundingClientRect().width)
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(node)
    observerRef.current = observer
  }, [])

  return [measureRef, computeVisibleSwatchCount(width, total)]
}
